import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { connectTeams, type Me } from "../lib/api";

/** "Connect with Teams", in the top bar until it's done: Microsoft's sign-in
 * and consent for the work account whose calendar holds your Teams meetings.
 * Not a way to sign in — you're already in, with Google. */
export function TeamsButton({ me }: { me: Me | null }) {
  const [going, setGoing] = useState(false);
  if (!me?.teams?.available || me.teams.connected) return null;
  return (
    <button
      className="teams-connect"
      disabled={going}
      onClick={() => {
        setGoing(true);
        connectTeams();
      }}
      title="Add the meetings on your Teams calendar, so the bot joins them too"
    >
      <TeamsLogo />
      <span>{going ? "Opening Microsoft…" : "Connect with Teams"}</span>
    </button>
  );
}

const MESSAGES: Record<string, { text: string; good: boolean }> = {
  connected: { text: "Teams calendar connected. Your Teams meetings will appear in a minute or so.", good: true },
  failed: {
    text: "Teams didn't connect. If Microsoft said the app needs admin approval, an admin of your Microsoft account "
      + "has to approve it once; otherwise, try again.",
    good: false,
  },
  "wrong-account": {
    text: "That Microsoft account isn't from your company's Microsoft 365. Connect with your work account.",
    good: false,
  },
};

/** How "Connect with Teams" went, on the way back from Microsoft (?teams=…),
 * once; the address is cleaned up after. */
export function TeamsNotice() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{ text: string; good: boolean } | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const outcome = params.get("teams");
    if (!outcome) return;
    setMessage(MESSAGES[outcome] ?? null);
    if (outcome === "connected") {
      queryClient.invalidateQueries({ queryKey: ["me"] });
      // The first read takes a moment; ask again shortly after.
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["recordings"] }), 8_000);
    }
    params.delete("teams");
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [location.search, location.pathname, navigate, queryClient]);

  if (!message) return null;
  return (
    <div className={`teams-notice${message.good ? " good" : ""}`} role="status">
      <TeamsLogo />
      <span>{message.text}</span>
      <button className="btn-text" onClick={() => setMessage(null)} aria-label="Dismiss">✕</button>
    </div>
  );
}

/** The Teams mark: a purple tile with a T, and a person beside it. */
export function TeamsLogo({ size = 18 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <circle cx="18" cy="6.2" r="2.6" fill="#7b83eb" />
      <rect x="13.5" y="9.8" width="9" height="9.5" rx="2.6" fill="#7b83eb" />
      <rect x="1.5" y="4.5" width="14" height="15" rx="2.6" fill="#5059c9" />
      <path d="M5 8.8h7M8.5 8.8v7.4" stroke="#fff" strokeWidth="2.1" strokeLinecap="round" />
    </svg>
  );
}
